import React from 'react';
import styled from 'styled-components';
import { Banner, Button } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';
import type { BiImportFailure } from '@repo/front-end-services';

import { biFailureReason, biFailureScope } from './biPushCopy';

/**
 * The Launch step's notice when the twin went live but BI was not updated —
 * `twin-launch-history-bi-push` R5 (maria's ruling, 2026-10-05: the person stays on Launch; the
 * success summary waits until they retry or continue).
 *
 * A `Banner` (error, on-glass — the surface every Launch-page banner uses, inside the step's one
 * glass panel; `onboarding-persistent-scene-wash-panels-glass`, R5.2) with an action row.
 * **The action row is built ahead of design** — `epic-design-system.md`'s inline-`Banner`-with-
 * action entry, owed Rob's ratification. It is recorded there as the seventh consumer, and the first with two
 * actions in the row; it follows the shipped practice exactly (ghost / `xs` `Button`s in a margin-only row,
 * left-aligned under the body) so it converges onto the `actions` slot when one is ruled.
 */

interface Props {
  className?: string;
  failure: BiImportFailure;
  /**
   * How many times the BI step has failed. A second or later failure is announced through its own
   * alert region, keyed on this count, so even an identical repeat is heard.
   */
  failures: number;
  /** A retry is running — both buttons hold, and the polite region says so. */
  running: boolean;
  /**
   * The launch's BI result is being saved to the launch history after the person pressed Retry (and
   * it succeeded) or Continue (`twin-launch-history-bi-outcome`, R3.8). The pressed button stays
   * `loading` until the summary opens; the other is disabled. Absent reads as not saving.
   */
  saving?: 'retry' | 'continue' | null;
  onRetry: () => void;
  onContinue: () => void;
}

const title = (failure: BiImportFailure): string =>
  failure.outcomeUnknown
    ? 'Your twin is live, but BI may not have been updated.'
    : 'Your twin is live, but BI was not updated.';

const LaunchBiNotice: React.FC<Props> = ({
  className,
  failure,
  failures,
  running,
  saving = null,
  onRetry,
  onContinue,
}) => (
  <div className={className}>
    <Banner surface="on-glass" tone="error" title={title(failure)}>
      {' '}
      {biFailureScope(failure)} {biFailureReason(failure)}
      <div className="bi-actions">
        {/*
         * `loading`, not `disabled`: a disabled button drops focus to the page, so a keyboard user
         * whose retry failed again would be sent back to the top of the wizard. `loading` keeps it
         * focusable, sets aria-busy and swallows a second press (Block A criterion 1).
         */}
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onRetry}
          loading={running || saving === 'retry'}
          disabled={saving === 'continue'}
        >
          {running || saving === 'retry' ? 'Updating BI…' : 'Retry BI update'}
        </Button>
        {/*
         * `loading` while its own choice is being saved, for the reason above: it keeps focus and
         * swallows a second press. Disabled only while the OTHER button's work runs.
         */}
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onContinue}
          loading={saving === 'continue'}
          disabled={running || saving === 'retry'}
        >
          Continue without BI update
        </Button>
      </div>
    </Banner>
    <span role="status" style={visuallyHidden}>
      {running ? 'Updating BI…' : saving ? 'Finishing the launch…' : ''}
    </span>
    {/*
     * A retry that fails AGAIN is announced here, not by remounting the Banner: a remount would
     * destroy the Retry button the person is focused on. The Banner's own role="alert" announces
     * the first failure; this region, keyed on the failure count, mounts afresh for every later
     * one — including an identical failure, the commonest repeat (a second lock contention), whose
     * unchanged text a live region would not re-read.
     */}
    {failures > 1 && !running && !saving && (
      <span key={failures} role="alert" style={visuallyHidden}>
        {`${title(failure)} ${biFailureScope(failure)} ${biFailureReason(failure)}`}
      </span>
    )}
  </div>
);

export default styled(LaunchBiNotice)`
  .bi-actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${space[2]};
    margin-top: ${space[2]};
  }
`;
